import type { AxiosInstance, AxiosResponse } from "axios";
import axios from "axios";
import { navigationService } from "./navigationService";
import type { AppDispatch } from "../store/store";
import { setCredentials, clearCredentials } from "../store/auth/authSlice";

const API_BASE_URL = "/api";

// Store reference for Redux dispatch
let store: { dispatch: AppDispatch } | null = null;

// Function to set store reference
export const setStoreForAPI = (storeInstance: { dispatch: AppDispatch }) => {
	store = storeInstance;
};

// Create axios instance
const axiosInstance: AxiosInstance = axios.create({
	baseURL: API_BASE_URL,
	timeout: 10000,
	headers: {
		"Content-Type": "application/json",
	},
});

// Request interceptor to add auth token
axiosInstance.interceptors.request.use(
	(config) => {
		const token = localStorage.getItem("token");
		if (token) {
			config.headers.Authorization = `Bearer ${token}`;
		}
		return config;
	},
	(error) => {
		return Promise.reject(error);
	},
);

// One refresh at a time. The server rotates the refresh token on every use
// and treats a replayed one as stolen - ending every session - so parallel
// 401s each refreshing on their own would log the user out.
let refreshInFlight: Promise<{ token: string; user: unknown }> | null = null;

export const refreshSession = (): Promise<{ token: string; user: unknown }> => {
	if (!refreshInFlight) {
		// The refresh token rides in its httpOnly cookie; the expired access
		// token is no longer proof of anything
		refreshInFlight = axios
			.post(`${API_BASE_URL}/auth/refresh`, {}, { withCredentials: true })
			.then((response) => {
				const { token, user } = response.data.data;
				localStorage.setItem("token", token);
				if (store && user) {
					store.dispatch(setCredentials({ user, token }));
				}
				return { token, user };
			})
			.finally(() => {
				refreshInFlight = null;
			});
	}
	return refreshInFlight;
};

const redirectToLogin = () => {
	if (store) {
		store.dispatch(clearCredentials());
	}
	localStorage.removeItem("token");
	const currentPath = navigationService.getCurrentPath();
	if (currentPath !== "/login") {
		localStorage.setItem("redirectPath", currentPath);
	}
	navigationService.navigateTo("/login", { replace: true });
};

// Blob requests (Excel exports) get their JSON error body back as a Blob.
// Parsed here so the token-expiry code is seen - it used to fall through to
// "log out" - and callers can read the server's message.
const toJsonErrorBody = async (error: {
	response?: { data?: unknown };
}): Promise<{ code?: string; message?: string } | undefined> => {
	const data = error.response?.data;
	if (data instanceof Blob) {
		try {
			const parsed = JSON.parse(await data.text());
			if (error.response) {
				error.response.data = parsed;
			}
			return parsed;
		} catch {
			return undefined;
		}
	}
	return data as { code?: string; message?: string } | undefined;
};

const SESSION_ENDPOINTS = ["/auth/login", "/auth/refresh"];

axiosInstance.interceptors.response.use(
	(response) => response,
	async (error) => {
		const originalRequest = error.config;
		const body = await toJsonErrorBody(error);
		const isSessionEndpoint = SESSION_ENDPOINTS.some((path) =>
			originalRequest?.url?.includes(path),
		);

		if (
			error.response?.status !== 401 ||
			!originalRequest ||
			originalRequest._retry ||
			isSessionEndpoint
		) {
			return Promise.reject(error);
		}
		originalRequest._retry = true;

		if (body?.code !== "TOKEN_EXPIRED") {
			redirectToLogin();
			return Promise.reject(error);
		}

		try {
			const { token } = await refreshSession();
			originalRequest.headers.Authorization = `Bearer ${token}`;
			return axiosInstance(originalRequest);
		} catch (refreshError) {
			redirectToLogin();
			return Promise.reject(refreshError);
		}
	},
);

// API Response types
interface ApiResponse<T = unknown> {
	success: boolean;
	message?: string;
	data: T;
}

interface LoginResponse {
	accessToken: string;
	user: {
		id: string;
		username: string;
		email: string;
		role: string;
		isActive: boolean;
		createdAt: string;
	};
	token: string;
}

// Query parameters interfaces
interface PaginationParams {
	page?: number;
	limit?: number;
	search?: string;
}

interface UserCreateData {
	username: string;
	email: string;
	password: string;
	role: string;
}

interface UserUpdateData {
	username: string;
	email: string;
	role: string;
	isActive: boolean;
}

export interface AccountOption {
	_id: string;
	name: string;
	isActive: boolean;
}

interface AccountCreateData {
	name: string;
	description?: string;
	contactEmail: string;
	maxSeats?: number;
	tags?: string[];
	reportEmails?: string[];
}

interface SendReportData {
	reportType: "billing" | "status";
	emails?: string[];
	toSubscribers?: boolean;
	scheduleMonthly?: boolean;
	sendNow?: boolean;
}

interface SendReportResponse {
	sent: string[];
	failed: { email: string; reason: string }[];
	subscribers: { _id: string; email: string; lastSentAt?: string | null }[];
}

interface LicenseTypeCreateData {
	key: string;
	name: string;
	description?: string;
	monthlyPrice: number;
	features?: string[];
	isActive?: boolean;
}

export interface BillingReportSummary {
	_id: string;
	period: string;
	filename: string;
	totalLicenses: number;
	activeLicenses: number;
	createdAt: string;
}

interface LicenseCreateData {
	name: string;
	accountId: string;
	os: string;
	licenseTypeId?: string;
	isUnlimited?: boolean;
	expirationDate?: string;
	customDuration?: number;
	tags?: string[];
}

export type ActivityCategory =
	| "license"
	| "station"
	| "account"
	| "user"
	| "auth"
	| "report";

// "spaceos" is the API caller on entries from before keys were named
export type ActivityActorType = "user" | "apiKey" | "spaceos" | "station" | "system";

export type ActivityAction =
	| "create-license"
	| "update-license"
	| "renew-license"
	| "revoke-license"
	| "reuse-license"
	| "hold-license"
	| "release-license"
	| "delete-license"
	| "bulk-revoke-licenses"
	| "bulk-hold-licenses"
	| "bulk-release-licenses"
	| "bulk-renew-licenses"
	| "bulk-delete-licenses"
	| "bulk-set-license-type"
	| "activate-station"
	| "deactivate-station"
	| "create-account"
	| "update-account"
	| "delete-account"
	| "send-report"
	| "remove-report-subscriber"
	| "create-user"
	| "update-user"
	| "delete-user"
	| "login"
	| "login-failed"
	| "change-password"
	| "logout-all"
	| "create-api-key"
	| "activate-api-key"
	| "deactivate-api-key";

export interface ActivityDetails {
	count?: number;
	changed?: string[];
	expirationDate?: string;
	recipients?: number;
	clientUid?: string;
	licenseType?: string;
}

export interface ActivityLogEntry {
	id: string;
	action: ActivityAction;
	category: ActivityCategory;
	// The username, the API key's name, or the station's clientUid, depending on actorType
	actorName: string;
	actorType: ActivityActorType;
	// null for events with nothing to point at, e.g. a failed login
	resource: string | null;
	// Absent on events with nothing to point at, such as a failed sign-in
	resourceId?: string;
	accountId?: string;
	details?: ActivityDetails;
	createdAt: string;
}

export interface ActivityLogPage {
	logs: ActivityLogEntry[];
	total: number;
	pagination: { current: number; pages: number; limit: number };
}

// Auth API
export const authAPI = {
	login: (
		username: string,
		password: string,
	): Promise<AxiosResponse<ApiResponse<LoginResponse>>> =>
		axiosInstance.post("/auth/login", { username, password }),

	logout: (): Promise<AxiosResponse<ApiResponse<null>>> =>
		axiosInstance.post("/auth/logout"),
};

// Users API
export const usersAPI = {
	getAll: (
		params?: PaginationParams & { role?: string },
	): Promise<AxiosResponse<ApiResponse<unknown>>> =>
		axiosInstance.get("/users", { params }),

	getById: (id: string): Promise<AxiosResponse<ApiResponse<unknown>>> =>
		axiosInstance.get(`/users/${id}`),

	create: (
		userData: UserCreateData,
	): Promise<AxiosResponse<ApiResponse<unknown>>> =>
		axiosInstance.post("/users", userData),

	update: (
		id: string,
		userData: UserUpdateData,
	): Promise<AxiosResponse<ApiResponse<unknown>>> =>
		axiosInstance.put(`/users/${id}`, userData),

	delete: (id: string): Promise<AxiosResponse<ApiResponse<null>>> =>
		axiosInstance.delete(`/users/${id}`),

	// The same filters as getAll, so the Overview can count what the table shows
	getStats: (params?: {
		search?: string;
		role?: string;
	}): Promise<AxiosResponse<ApiResponse<unknown>>> =>
		axiosInstance.get("/users/stats", { params }),
};

// Accounts API
export const accountsAPI = {
	getAll: (
		params?: PaginationParams & { isActive?: boolean; expiring?: boolean },
	): Promise<AxiosResponse<ApiResponse<unknown>>> =>
		axiosInstance.get("/accounts", { params }),

	getById: (id: string): Promise<AxiosResponse<ApiResponse<unknown>>> =>
		axiosInstance.get(`/accounts/${id}`),

	create: (
		accountData: AccountCreateData,
	): Promise<AxiosResponse<ApiResponse<unknown>>> =>
		axiosInstance.post("/accounts", accountData),

	update: (
		id: string,
		accountData: AccountCreateData & { isActive: boolean },
	): Promise<AxiosResponse<ApiResponse<unknown>>> =>
		axiosInstance.put(`/accounts/${id}`, accountData),

	delete: (id: string): Promise<AxiosResponse<ApiResponse<null>>> =>
		axiosInstance.delete(`/accounts/${id}`),

	// Every account, names only - for dropdowns and filters
	getOptions: (): Promise<AxiosResponse<ApiResponse<{ accounts: AccountOption[] }>>> =>
		axiosInstance.get("/accounts/options"),

	// The same filters as getAll, so the Overview can count what the table shows
	getStats: (params?: {
		search?: string;
	}): Promise<AxiosResponse<ApiResponse<unknown>>> =>
		axiosInstance.get("/accounts/stats", { params }),

	exportLicenses: (id: string): Promise<AxiosResponse<Blob>> =>
		axiosInstance.get(`/accounts/${id}/export`, {
			responseType: "blob",
		}),

	exportStatusReport: (id: string): Promise<AxiosResponse<Blob>> =>
		axiosInstance.get(`/accounts/${id}/status-report`, {
			responseType: "blob",
		}),

	getBillingReports: (
		id: string,
	): Promise<AxiosResponse<ApiResponse<{ reports: BillingReportSummary[] }>>> =>
		axiosInstance.get(`/accounts/${id}/billing-reports`),

	downloadBillingReport: (
		id: string,
		period: string,
	): Promise<AxiosResponse<Blob>> =>
		axiosInstance.get(`/accounts/${id}/billing-reports/${period}`, {
			responseType: "blob",
		}),

	sendReport: (
		id: string,
		payload: SendReportData,
	): Promise<AxiosResponse<ApiResponse<SendReportResponse>>> =>
		// A multi-recipient send against Office 365 outruns the shared 10s timeout; an
		// aborted request leaves the server sending and invites a duplicate-email retry.
		axiosInstance.post(`/accounts/${id}/send-report`, payload, {
			timeout: 120000,
		}),

	removeScheduledReport: (
		accountId: string,
		email: string,
	): Promise<AxiosResponse<ApiResponse<{ message: string }>>> =>
		axiosInstance.delete(
			`/scheduled-reports/account/${accountId}/email/${encodeURIComponent(
				email,
			)}`,
		),
};

// Licenses API
export const licensesAPI = {
	getAll: (
		params?: PaginationParams & {
			accountId?: string;
			licenseTypeId?: string;
			os?: string;
			status?: string;
			isActive?: boolean;
		},
	): Promise<AxiosResponse<ApiResponse<unknown>>> =>
		axiosInstance.get("/licenses", { params }),

	getById: (id: string): Promise<AxiosResponse<ApiResponse<unknown>>> =>
		axiosInstance.get(`/licenses/${id}`),

	create: (
		licenseData: LicenseCreateData,
	): Promise<AxiosResponse<ApiResponse<unknown>>> =>
		axiosInstance.post("/licenses", licenseData),

	update: (
		id: string,
		licenseData: Partial<LicenseCreateData> & { isActive?: boolean },
	): Promise<AxiosResponse<ApiResponse<unknown>>> =>
		axiosInstance.put(`/licenses/${id}`, licenseData),

	delete: (id: string): Promise<AxiosResponse<ApiResponse<null>>> =>
		axiosInstance.delete(`/licenses/${id}`),

	verify: (token: string): Promise<AxiosResponse<ApiResponse<unknown>>> =>
		axiosInstance.post("/licenses/verify", { token }),

	// The same filters as getAll, so the Overview can count what the table shows
	getStats: (params?: {
		search?: string;
		status?: string;
		accountId?: string;
		licenseTypeId?: string;
	}): Promise<AxiosResponse<ApiResponse<unknown>>> =>
		axiosInstance.get("/licenses/stats", { params }),

	bulkRevoke: (
		licenseIds: string[],
	): Promise<AxiosResponse<ApiResponse<{ count: number }>>> =>
		axiosInstance.post("/licenses/bulk-revoke", { licenseIds }),

	bulkHold: (
		licenseIds: string[],
	): Promise<AxiosResponse<ApiResponse<{ count: number }>>> =>
		axiosInstance.post("/licenses/bulk-hold", { licenseIds }),

	bulkRelease: (
		licenseIds: string[],
	): Promise<AxiosResponse<ApiResponse<{ count: number }>>> =>
		axiosInstance.post("/licenses/bulk-release", { licenseIds }),

	bulkRenew: (
		licenseIds: string[],
		expirationDate: string,
	): Promise<AxiosResponse<ApiResponse<{ renewed: number; skipped: number; failed: number }>>> =>
		axiosInstance.post("/licenses/bulk-renew", { licenseIds, expirationDate }),

	bulkDelete: (
		licenseIds: string[],
	): Promise<AxiosResponse<ApiResponse<unknown>>> =>
		axiosInstance.post("/licenses/bulk-delete", { licenseIds }),

	bulkSetType: (
		licenseIds: string[],
		licenseTypeId: string,
	): Promise<AxiosResponse<ApiResponse<{ count: number }>>> =>
		axiosInstance.post("/licenses/bulk-set-type", { licenseIds, licenseTypeId }),

	getIds: (params: {
		search?: string;
		accountId?: string;
		licenseTypeId?: string;
		status?: string;
	}): Promise<AxiosResponse<ApiResponse<{ ids: string[] }>>> =>
		axiosInstance.get("/licenses/ids", { params }),

	hold: (
		id: string,
		action: "hold" | "release",
	): Promise<AxiosResponse<ApiResponse<unknown>>> =>
		axiosInstance.post(`/licenses/${id}/hold`, { action }),

	getToken: (
		id: string,
	): Promise<AxiosResponse<ApiResponse<{ licenseToken: string }>>> =>
		axiosInstance.get(`/licenses/${id}/token`),
};

// License Types API
export const licenseTypesAPI = {
	getAll: (
		params?: PaginationParams & { isActive?: boolean },
	): Promise<AxiosResponse<ApiResponse<unknown>>> =>
		axiosInstance.get("/license-types", { params }),

	getFeatures: (): Promise<AxiosResponse<ApiResponse<unknown>>> =>
		axiosInstance.get("/license-types/features"),

	create: (
		licenseTypeData: LicenseTypeCreateData,
	): Promise<AxiosResponse<ApiResponse<unknown>>> =>
		axiosInstance.post("/license-types", licenseTypeData),

	update: (
		id: string,
		licenseTypeData: Partial<Omit<LicenseTypeCreateData, "key">>,
	): Promise<AxiosResponse<ApiResponse<unknown>>> =>
		axiosInstance.put(`/license-types/${id}`, licenseTypeData),

	delete: (id: string): Promise<AxiosResponse<ApiResponse<null>>> =>
		axiosInstance.delete(`/license-types/${id}`),
};

// Activity log API - admin and manager only; the backend answers viewers with 403
export interface ApiKey {
	_id: string;
	name: string;
	hint: string;
	isActive: boolean;
	lastUsedAt: string | null;
	createdBy: { _id: string; username: string } | null;
	createdAt: string;
}

// API keys for service callers such as spaceOS - admin only
export const apiKeysAPI = {
	getAll: (): Promise<AxiosResponse<ApiResponse<{ apiKeys: ApiKey[] }>>> =>
		axiosInstance.get("/api-keys"),

	// The response carries the key itself; it cannot be fetched again
	create: (
		name: string,
	): Promise<AxiosResponse<ApiResponse<{ apiKey: ApiKey; key: string }>>> =>
		axiosInstance.post("/api-keys", { name }),

	setActive: (
		id: string,
		isActive: boolean,
	): Promise<AxiosResponse<ApiResponse<{ apiKey: ApiKey }>>> =>
		axiosInstance.patch(`/api-keys/${id}`, { isActive }),
};

export const activityAPI = {
	getAll: (params?: {
		page?: number;
		limit?: number;
		category?: ActivityCategory;
	}): Promise<AxiosResponse<ApiResponse<ActivityLogPage>>> =>
		axiosInstance.get("/activity-log", { params }),
};

export default axiosInstance;
